import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../api/axios';
import { toast } from 'react-toastify';

const inp = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '14px',
  boxSizing: 'border-box'
};

const lbl = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '14px',
  color: '#444',
  fontWeight: 600
};

const ROLES = [
  { key: 'invigilator', label: 'Invigilator' },
  { key: 'tca', label: 'TCA' },
  { key: 'system_admin', label: 'System Admin' },
  { key: 'attender', label: 'Attender' }
];

const SHIFT_OPTIONS = [
  { number: 1, name: 'Morning Shift' },
  { number: 2, name: 'Afternoon Shift' },
  { number: 3, name: 'Evening Shift' },
  { number: 4, name: 'Night Shift' }
];

const generateDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return [];
  const dates = [];
  const current = new Date(startDate);
  const end = new Date(endDate);
  while (current <= end) {
    dates.push(new Date(current).toISOString().split('T')[0]);
    current.setDate(current.getDate() + 1);
  }
  return dates;
};

const formatDateLabel = (dateStr) => {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-IN', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const AddShift = () => {

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedShifts, setSelectedShifts] = useState([]); // e.g. [1, 2]

  const [form, setForm] = useState({
    schedule_id: '',
    shift_date: '',
    start_time: '',
    end_time: '',
    student_count: '',
    is_holiday: false
  });

  const [remuneration, setRemuneration] = useState({
    invigilator: '',
    tca: '',
    system_admin: '',
    attender: ''
  });

  // Fetch schedules
  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        const res = await axios.get('/schedules');
        setSchedules(res.data || []);
      } catch (err) {
        console.log(err);
        toast.error('Failed to load schedules');
      }
    };
    fetchSchedules();
  }, []);

  // When schedule changes, generate date range
  useEffect(() => {
    if (!form.schedule_id) {
      setAvailableDates([]);
      setForm(prev => ({ ...prev, shift_date: '' }));
      return;
    }
    const selected = schedules.find(s => String(s.id) === String(form.schedule_id));
    if (selected && selected.start_date && selected.end_date) {
      const dates = generateDateRange(selected.start_date, selected.end_date);
      setAvailableDates(dates);
      setForm(prev => ({ ...prev, shift_date: dates[0] || '' }));
    } else {
      setAvailableDates([]);
      setForm(prev => ({ ...prev, shift_date: '' }));
    }
  }, [form.schedule_id, schedules]);

  const selectedSchedule = schedules.find(s => String(s.id) === String(form.schedule_id));
  const remunerationType = selectedSchedule?.remuneration_type || 'per_shift';

  // Handle input
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle remuneration change per role
  const handleRemunerationChange = (e) => {
    const { name, value } = e.target;
    setRemuneration(prev => ({ ...prev, [name]: value }));
  };

  const toggleShift = (num) => {
    setSelectedShifts(prev =>
      prev.includes(num)
        ? prev.filter(n => n !== num)
        : [...prev, num].sort((a, b) => a - b)
    );
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.schedule_id) {
      toast.error('Please select schedule');
      return;
    }

    if (!form.shift_date) {
      toast.error('Please select shift date');
      return;
    }

    if (selectedShifts.length === 0) {
      toast.error('Please select at least one shift');
      return;
    }

    if (form.start_time >= form.end_time) {
      toast.error('End time must be after start time');
      return;
    }

    setLoading(true);

    try {
      for (const shiftNumber of selectedShifts) {
        const shiftMeta = SHIFT_OPTIONS.find(s => s.number === shiftNumber);

        const payload = {
          schedule_id: Number(form.schedule_id),
          shift_date: form.shift_date,
          shift_number: shiftNumber,
          shift_name: shiftMeta?.name || `Shift ${shiftNumber}`,
          start_time: form.start_time,
          end_time: form.end_time,
          student_count: Number(form.student_count || 0),
          is_holiday: form.is_holiday,
          remuneration_rates: {
            invigilator: Number(remuneration.invigilator || 0),
            tca: Number(remuneration.tca || 0),
            system_admin: Number(remuneration.system_admin || 0),
            attender: Number(remuneration.attender || 0)
          }
        };

        console.log(payload);
        await axios.post('/shifts', payload);
      }

      toast.success(`${selectedShifts.length} shift${selectedShifts.length > 1 ? 's' : ''} created successfully`);
      navigate('/shifts');

    } catch (err) {
      console.log(err);
      toast.error(
        err.response?.data?.message || 'Failed to create shift(s)'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '32px', backgroundColor: '#f5f6fa', minHeight: '100vh' }}>
      <div style={{ maxWidth: '700px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px' }}>
          <button
            onClick={() => navigate('/shifts')}
            style={{
              background: 'none',
              border: '1px solid #ddd',
              padding: '6px 14px',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            ← Back
          </button>
          <h2 style={{ margin: 0, color: '#1a237e' }}>Add Shift</h2>
        </div>

        {/* Form */}
        <div style={{ background: 'white', padding: '32px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <form onSubmit={handleSubmit}>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

              {/* Schedule */}
              <div style={{ gridColumn: '1/-1' }}>
                <label style={lbl}>Schedule *</label>
                <select
                  name="schedule_id"
                  value={form.schedule_id}
                  onChange={handleChange}
                  required
                  style={{ ...inp, backgroundColor: 'white' }}
                >
                  <option value="">Select Schedule</option>
                  {schedules.map(schedule => (
                    <option key={schedule.id} value={schedule.id}>
                      {schedule.exam_name}
                    </option>
                  ))}
                </select>
                {selectedSchedule && (
                  <span style={{
                    display: 'inline-block',
                    marginTop: '8px',
                    fontSize: '12px',
                    fontWeight: 700,
                    padding: '3px 10px',
                    borderRadius: '20px',
                    background: remunerationType === 'per_day' ? '#fff8e1' : '#eef2ff',
                    color: remunerationType === 'per_day' ? '#f57f17' : '#1a237e'
                  }}>
                    Remuneration: {remunerationType === 'per_day' ? 'Per Day' : 'Per Shift'} (set when the exam was created)
                  </span>
                )}
              </div>

              {/* Shift Date — dropdown from schedule date range */}
              <div style={{ gridColumn: '1/-1' }}>
                <label style={lbl}>Shift Date *</label>
                {availableDates.length > 0 ? (
                  <select
                    name="shift_date"
                    value={form.shift_date}
                    onChange={handleChange}
                    required
                    style={{ ...inp, backgroundColor: 'white' }}
                  >
                    <option value="">Select Date</option>
                    {availableDates.map(date => (
                      <option key={date} value={date}>
                        {formatDateLabel(date)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select
                    disabled
                    style={{ ...inp, backgroundColor: '#f0f0f0', color: '#999' }}
                  >
                    <option>Select a schedule first</option>
                  </select>
                )}
              </div>

              {/* Shifts — checkboxes */}
              <div style={{ gridColumn: '1/-1' }}>
                <label style={lbl}>Shifts *</label>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  {SHIFT_OPTIONS.map(opt => (
                    <label
                      key={opt.number}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 14px',
                        border: `1px solid ${selectedShifts.includes(opt.number) ? '#1a237e' : '#ddd'}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        backgroundColor: selectedShifts.includes(opt.number) ? '#eef2ff' : 'white'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedShifts.includes(opt.number)}
                        onChange={() => toggleShift(opt.number)}
                      />
                      Shift {opt.number} — {opt.name}
                    </label>
                  ))}
                </div>
                {remunerationType === 'per_day' && selectedShifts.length > 1 && (
                  <p style={{ fontSize: '12px', color: '#f57f17', marginTop: '8px' }}>
                    This exam pays per day. Once you assign staff to any one of these shifts (in Invigilator Mapping),
                    the same staff will automatically cover all shifts you create for this date.
                  </p>
                )}
              </div>

              {/* Student Count */}
              <div>
                <label style={lbl}>Student Count</label>
                <input
                  type="number"
                  name="student_count"
                  value={form.student_count}
                  onChange={handleChange}
                  placeholder="Enter student count"
                  style={inp}
                />
              </div>

              {/* Holiday */}
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '28px' }}>
                <input
                  type="checkbox"
                  name="is_holiday"
                  checked={form.is_holiday}
                  onChange={handleChange}
                />
                <label style={{ marginLeft: '8px' }}>Holiday Shift</label>
              </div>

              {/* Start Time */}
              <div>
                <label style={lbl}>Start Time * <span style={{ fontWeight: 400, color: '#999' }}>(applies to all selected shifts)</span></label>
                <input
                  type="time"
                  name="start_time"
                  value={form.start_time}
                  onChange={handleChange}
                  required
                  style={inp}
                />
              </div>

              {/* End Time */}
              <div>
                <label style={lbl}>End Time *</label>
                <input
                  type="time"
                  name="end_time"
                  value={form.end_time}
                  onChange={handleChange}
                  required
                  style={inp}
                />
              </div>

            </div>

            {/* Remuneration Section */}
            <div style={{ marginTop: '28px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '16px'
              }}>
                <div style={{
                  height: '1px',
                  flex: 1,
                  backgroundColor: '#e0e0e0'
                }} />
                <span style={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: '#1a237e',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  whiteSpace: 'nowrap'
                }}>
                  Remuneration Rates (₹)
                </span>
                <div style={{
                  height: '1px',
                  flex: 1,
                  backgroundColor: '#e0e0e0'
                }} />
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '20px'
              }}>
                {ROLES.map(role => (
                  <div key={role.key}>
                    <label style={lbl}>{role.label}</label>
                    <div style={{ position: 'relative' }}>
                      <span style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '14px',
                        color: '#666',
                        fontWeight: 600,
                        pointerEvents: 'none'
                      }}>
                        ₹
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        name={role.key}
                        value={remuneration[role.key]}
                        onChange={handleRemunerationChange}
                        placeholder="0.00"
                        style={{
                          ...inp,
                          paddingLeft: '28px'
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '30px' }}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#1a237e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 700
                }}
              >
                {loading ? 'Creating...' : `Create ${selectedShifts.length > 1 ? `${selectedShifts.length} Shifts` : 'Shift'}`}
              </button>
              <button
                type="button"
                onClick={() => navigate('/shifts')}
                style={{
                  padding: '12px 24px',
                  border: '1px solid #ddd',
                  background: 'white',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>

          </form>
        </div>

      </div>
    </div>
  );
};

export default AddShift;